// Shared look & feel for the participant-facing surface:
// /participate/:slug (InterviewLandingPage) and /interview/:id (InterviewPage).
//
// One accent, a soft type scale (body 400–500, headings 600–700), green only
// for passed checks / success, red only for true errors — no shouting.
// Introduced in the 2026-08 UX refresh (AI_INTERVIEWER_UX_PLAN.md, Phase 5).
//
// Pages spread `ui` into their local styles object (`{ ...ui, ...pageStyles }`)
// so page-specific entries stay in the page and shared ones live here once.
import React from "react";

export const colors = {
  accent: "#2563EB",
  accentDark: "#1D4ED8",
  accentSoft: "#EFF6FF",
  canvas: "#F6F7F9",
  line: "#E5E7EB",
  lineStrong: "#D1D5DB",
  ink: "#111827",
  body: "#374151",
  muted: "#6B7280",
  faint: "#9CA3AF",
  good: "#059669",
  goodSoft: "#ECFDF5",
  warn: "#B45309",
  bad: "#B91C1C",
};

export const ui = {
  page: {
    minHeight: "100vh",
    background: colors.canvas,
    padding: "32px 16px",
  },

  card: {
    background: "#fff",
    border: `1px solid ${colors.line}`,
    borderRadius: 16,
    padding: 22,
    boxShadow: "0 1px 2px rgba(0,0,0,.04)",
  },

  kicker: {
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: "0.06em",
    color: colors.accent,
    textTransform: "uppercase",
    marginBottom: 8,
  },

  sectionTitle: {
    fontSize: 15,
    fontWeight: 650,
    color: colors.ink,
    marginBottom: 6,
  },

  divider: { height: 1, background: colors.line, margin: "14px 0" },

  fieldLabel: { fontSize: 13, fontWeight: 600, color: colors.body },
  input: {
    width: "100%",
    boxSizing: "border-box",
    display: "block",
    padding: "10px 12px",
    borderRadius: 10,
    border: `1px solid ${colors.lineStrong}`,
    fontSize: 14,
  },
  select: {
    width: "100%",
    boxSizing: "border-box",
    display: "block",
    padding: "10px 12px",
    borderRadius: 10,
    border: `1px solid ${colors.lineStrong}`,
    background: "#fff",
    fontSize: 14,
  },

  primaryBtn: {
    background: colors.accent,
    color: "#fff",
    border: `1px solid ${colors.accent}`,
    padding: "10px 18px",
    borderRadius: 10,
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
  },
  primaryBtnBig: {
    background: colors.accent,
    color: "#fff",
    border: `1px solid ${colors.accent}`,
    padding: "12px 24px",
    borderRadius: 10,
    fontSize: 15,
    fontWeight: 600,
    cursor: "pointer",
  },
  secondaryBtn: {
    background: "#fff",
    color: colors.ink,
    border: `1px solid ${colors.lineStrong}`,
    padding: "10px 14px",
    borderRadius: 10,
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
  },
  btnDisabled: { opacity: 0.5, cursor: "not-allowed" },
  linkBtn: {
    border: "none",
    background: "transparent",
    padding: 0,
    fontSize: 13,
    fontWeight: 600,
    color: colors.accent,
    cursor: "pointer",
    textDecoration: "underline",
    textUnderlineOffset: 3,
  },

  chip: {
    fontSize: 13,
    fontWeight: 600,
    padding: "6px 12px",
    borderRadius: 999,
    border: `1px solid ${colors.line}`,
    background: "#fff",
    color: colors.body,
  },
  chipHighlight: {
    background: colors.accentSoft,
    borderColor: "#BFDBFE",
    color: colors.accentDark,
  },

  alert: {
    padding: "10px 12px",
    borderRadius: 12,
    border: `1px solid ${colors.line}`,
    fontSize: 13,
    lineHeight: 1.45,
  },
  alertInfo: { background: "#EFF6FF", borderColor: "#BFDBFE", color: "#1E3A8A" },
  alertWarn: { background: "#FFFBEB", borderColor: "#FDE68A", color: "#92400E" },
  alertError: { background: "#FEF2F2", borderColor: "#FECACA", color: "#991B1B" },
  alertSuccess: { background: "#ECFDF5", borderColor: "#A7F3D0", color: "#065F46" },

  details: { borderTop: `1px solid ${colors.line}`, paddingTop: 10, marginTop: 10 },
  summary: { cursor: "pointer", fontSize: 13, fontWeight: 600, color: colors.ink },
  detailsBlock: { marginTop: 10, display: "grid", gap: 12 },
  detailsTitle: { fontSize: 12, fontWeight: 650, color: colors.ink },
  detailsText: { fontSize: 13, fontWeight: 400, color: colors.ink, lineHeight: 1.45 },

  ul: {
    marginTop: 0,
    marginBottom: 0,
    paddingLeft: 18,
    color: colors.ink,
    fontSize: 13,
    fontWeight: 400,
    lineHeight: 1.5,
  },
};

export function Card({ children, style }) {
  return <div style={{ ...ui.card, ...style }}>{children}</div>;
}

export function InlineAlert({ type, children }) {
  const tone =
    type === "error"
      ? ui.alertError
      : type === "warning"
      ? ui.alertWarn
      : type === "success"
      ? ui.alertSuccess
      : ui.alertInfo;
  return <div style={{ ...ui.alert, ...tone }}>{children}</div>;
}

export function Chip({ children, highlight }) {
  return (
    <div style={{ ...ui.chip, ...(highlight ? ui.chipHighlight : {}) }}>
      {children}
    </div>
  );
}

export function Field({ label, children }) {
  return (
    <div style={{ display: "grid", gap: 6 }}>
      <div style={ui.fieldLabel}>{label}</div>
      {children}
    </div>
  );
}
